import {
	SET_ROLES,
	SET_PERMISSIONS,
	SET_RELOAD_ROLES,
	DELETE_ROLE,
	SET_RELOAD_PERMISSIONS,
	ROLE_HISTORY_LOGS,
	SET_ROLE_HISTORY_LOGS,
	TOAST_MESSAGES_PUSH,
	INTERNAL_ROLES,
	SET_INTERNAL_ROLES,
	CONSULTANT_ROLES,
	SET_CONSULTANT_ROLES,
	LOAD_ROLES_WITH_PERMISSIONS
} from './roles-mutation-types';
import { ApiService as api } from '@/services/axiosHelper';
import buildRoute from '@/composables/build-route';
import { mergePayloadObjects } from '@/utils/object';
import { isObject } from '@/utils/inspect';
import _ from 'lodash';

let messageObj = {
	statusCode: 400,
	message: 'An error occurred. Please try again.'
};

export default {
	strict: process.env.NODE_ENV !== 'production',
	name: 'roles',
	namespaced: true,
	state: {
		roles: {},
		permissions: {},
		reloadRoles: false,
		reloadPermissions: false,
		[ROLE_HISTORY_LOGS]: {},
		[INTERNAL_ROLES]: {},
		[CONSULTANT_ROLES]: {}
	},
	mutations: {
		[SET_ROLES](state, roles) {
			state.roles = roles;
		},
		[SET_PERMISSIONS](state, permissions) {
			state.permissions = permissions;
		},
		[SET_RELOAD_ROLES](state, value) {
			state.reloadRoles = value;
		},
		[SET_RELOAD_PERMISSIONS](state, value) {
			state.reloadPermissions = value;
		},
		[DELETE_ROLE](state, value) {
			const idx = state.roles.items.findIndex(
				item => item.roleId === value.roleId
			);
			if (idx > -1) {
				state.roles.items.splice(idx, 1);
			}
			state.roles.totalCount--;
		},
		[SET_ROLE_HISTORY_LOGS](state, obj) {
			state[ROLE_HISTORY_LOGS] = obj;
		},
		[SET_INTERNAL_ROLES](state, obj) {
			state[INTERNAL_ROLES] = obj;
		},
		[SET_CONSULTANT_ROLES](state, obj) {
			state[CONSULTANT_ROLES] = obj;
		}
	},
	actions: {
		async loadRoles({ dispatch, commit, state }, parameters) {
			const { page } = parameters;
			if (page === 1) {
				commit(SET_ROLES, {});
			}
			let results = {};
			await api
				.get(buildRoute('/supportroles', parameters))
				.then(res => {
					if (_.isEmpty(state.roles)) {
						commit(SET_ROLES, res);
						results = res;
					} else {
						let currentStore = _.cloneDeep(state.roles);
						let rolesPayload = mergePayloadObjects(
							currentStore,
							res
						);
						commit(SET_ROLES, rolesPayload);
						results = rolesPayload;
					}
				})
				.catch(error => {
					if (error) {
						messageObj.message = error;
					}
					dispatch(TOAST_MESSAGES_PUSH, messageObj, {
						root: true
					});
				});

			commit(SET_RELOAD_ROLES, false);
			return results;
		},
		async loadPermissions({ commit }, parameters) {
			const results = await api.get(
				buildRoute('/supportpermissions', parameters)
			);
			commit(SET_PERMISSIONS, results);
			return results;
		},
		reloadRoles({ commit }) {
			commit(SET_RELOAD_ROLES, true);
		},
		reloadPermissions({ commit }) {
			commit(SET_RELOAD_PERMISSIONS, true);
		},
		deleteRole({ commit }, role) {
			commit(DELETE_ROLE, role);
		},
		async [SET_ROLE_HISTORY_LOGS]({ dispatch, commit }, payload) {
			await api
				.post('/versionhistory', payload)
				.then(response => {
					commit(SET_ROLE_HISTORY_LOGS, response);
				})
				.catch(error => {
					if (error) {
						messageObj.message = error;
					}
					dispatch(TOAST_MESSAGES_PUSH, messageObj, {
						root: true
					});
				});
		},
		async saveRole({ commit, dispatch }, payload) {
			let result = {};
			let isSuccessful = true;
			let verbiage = 'successfully';
			let action = payload.roleId ? 'edited' : 'added';
			if (payload.roleId) {
				await api
					.put(`/supportroles/${payload.roleId}`, payload)
					.then(resp => {
						result = _.cloneDeep(resp);
						commit(SET_RELOAD_ROLES, true);
					})
					.catch(error => {
						isSuccessful = false;
						if (error && typeof error === 'string') {
							Object.assign(result, { message: error });
						}
					});
			} else {
				await api
					.post('/supportroles', payload)
					.then(resp => {
						result = _.cloneDeep(resp);
						commit(SET_RELOAD_ROLES, true);
					})
					.catch(error => {
						isSuccessful = false;
						if (isObject(error)) {
							Object.assign(result, error);
						}
						if (error && typeof error === 'string') {
							Object.assign(result, { message: error });
						}
					});
			}
			if (isSuccessful) {
				dispatch(
					TOAST_MESSAGES_PUSH,
					{
						statusCode: 200,
						message: `Role ${verbiage} ${action}`
					},
					{
						root: true
					}
				);
			}

			Object.assign(result, { isSuccessful: isSuccessful });
			return result;
		},
		async getRole({ dispatch }, roleId) {
			let result = {};
			await api
				.get(`/supportroles/${roleId}`)
				.then(resp => {
					result = _.cloneDeep(resp);
				})
				.catch(error => {
					let message = error
						? error
						: `RoleId ${roleId} does not exist.`;
					dispatch(
						TOAST_MESSAGES_PUSH,
						{
							statusCode: 400,
							message: message
						},
						{
							root: true
						}
					);
				});

			return result;
		},
		async removeRole({ dispatch }, id) {
			let isSuccessful = true;
			let verbiage = 'successfully';
			await api
				.delete(`/supportroles/${id}`, {})
				.then()
				.catch(() => {
					isSuccessful = false;
					verbiage = 'unsuccessfully';
				})
				.finally(() => {
					dispatch(
						TOAST_MESSAGES_PUSH,
						{
							statusCode: isSuccessful ? 200 : 400,
							message: `Role ${verbiage} removed`
						},
						{
							root: true
						}
					);
				});
		},
		async [LOAD_ROLES_WITH_PERMISSIONS]({ dispatch, commit }, parameters) {
			const { clientId } = parameters;
			const setRoleOpts = {
				'vue-support-portal': SET_INTERNAL_ROLES,
				'vue-support-external': SET_CONSULTANT_ROLES
			};

			await api
				.get(
					buildRoute(`/supportroles/withpac`, {
						clientId,
						...parameters
					})
				)
				.then(res => {
					commit(setRoleOpts[clientId], res);
				})
				.catch(error => {
					if (error) {
						messageObj.message = error;
					}
					dispatch(TOAST_MESSAGES_PUSH, messageObj, {
						root: true
					});
				});
		}
	},
	getters: {
		roles: state => state.roles,
		permissions: state => state.permissions,
		reloadRoles: state => state.reloadRoles,
		reloadPermissions: state => state.reloadPermissions,
		optionRoles: state =>
			(state.roles &&
				state.roles.items &&
				state.roles.items.map(item => {
					let { roleId, roleDescription } = item;
					return {
						text: roleDescription,
						value: roleId
					};
				})) ||
			[],
		[ROLE_HISTORY_LOGS]: state => state[ROLE_HISTORY_LOGS],
		permissionIdByPermissionCode: state => code => {
			if (!state.permissions.items) {
				return null;
			}
			for (let group of state.permissions.items) {
				// Iterate over permissions in the current group
				for (let permission of group.permissions) {
					// Check if the permission code matches the provided permission name
					if (permission.permissionCode === code) {
						return permission.permissionId;
					}
				}
			}
			return null;
		}
	}
};
